import { isEqual, isNumber } from 'lodash'
import React, {
  forwardRef,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

type DropdownPosition =
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'right-top'
  | 'right'
  | 'right-bottom'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left'
  | 'left-bottom'
  | 'left'
  | 'left-top'

type DropdownTriggerProps = {
  isOpen: boolean
  toggle: (e?: React.MouseEvent) => void
  open: (e?: React.MouseEvent) => void
  close: (e?: React.MouseEvent) => void
}

type DropdownContentProps = {
  isOpen: boolean
  close: (e?: React.MouseEvent) => void
}

export type DropdownRef = {
  open: (e?: React.MouseEvent) => void
  close: (e?: React.MouseEvent) => void
  toggle: (e?: React.MouseEvent) => void
  isOpen: boolean
}

export type DropdownProps = {
  allowContentClicks?: boolean
  className?: string
  content?: (props: DropdownContentProps) => ReactNode
  contentHeight?: string | number
  contentWidth?: number | 'sm' | 'md' | 'lg' | 'block' | 'auto'
  cover?: boolean
  disabled?: boolean
  fixedContent?: boolean
  inline?: boolean
  position?: DropdownPosition
  onClose?: (e?: React.MouseEvent) => void
  onOpen?: (e?: React.MouseEvent) => void
  renderContent?: (props: DropdownContentProps) => ReactNode
  renderTrigger: (props: DropdownTriggerProps) => ReactNode
  triggerIsBlock?: boolean
  defaultOpenState?: boolean
}

function getContentPositionClasses(
  fixedContent: boolean | undefined,
  direction: string,
  lean: string | undefined,
  openAbove: boolean,
  horizontalAbove: boolean,
  cover: boolean | undefined,
) {
  if (fixedContent) return ''

  const classes: string[] = []
  const isHorizontal = ['right', 'left'].includes(direction)

  // Direction-based positioning
  if (direction === 'right') {
    classes.push('left-full right-auto')
  } else if (direction === 'left') {
    classes.push('right-full left-auto')
  } else if (direction === 'bottom' && !openAbove) {
    classes.push('top-full')
  }

  // Above (computed or direction=top)
  if (openAbove) {
    classes.push('bottom-full top-auto')
  }

  // Horizontal above
  if (horizontalAbove) {
    classes.push('bottom-0 top-auto')
  }

  // Horizontal default top:0 (when not horizontalAbove and lean is not 'top')
  if (isHorizontal && !horizontalAbove && lean !== 'top') {
    classes.push('top-0')
  }

  // Cover overrides
  if (cover) {
    if (openAbove) {
      classes.push('!top-auto !bottom-0')
    } else if (direction === 'right') {
      classes.push('!left-0')
    } else if (direction === 'bottom') {
      classes.push('!top-0')
    } else if (direction === 'left') {
      classes.push('!right-0')
    }
  }

  // Lean positioning (non-fixed only)
  if (lean === 'top') classes.push('bottom-0')
  if (lean === 'right') classes.push('left-0')
  if (lean === 'bottom' && !horizontalAbove) classes.push('top-0')
  if (lean === 'left') classes.push('right-0')

  return classes.join(' ')
}

function _Dropdown(props: DropdownProps, dropdownRef: React.Ref<DropdownRef>) {
  const {
    allowContentClicks,
    className,
    content,
    contentHeight = 'auto',
    contentWidth = 'sm',
    cover,
    disabled,
    fixedContent,
    inline,
    position = 'bottom',
    onClose,
    onOpen,
    renderContent,
    renderTrigger,
    triggerIsBlock,
    defaultOpenState,
  } = props
  const dropdownEl = useRef<HTMLDivElement>(null)
  const triggerEl = useRef<HTMLSpanElement>(null)
  const contentEl = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [openAbove, setOpenAbove] = useState(false)
  const [horizontalAbove, setHorizontalAbove] = useState(false)
  const [positionStyles, setPositionStyles] = useState<Record<string, any>>({})
  const [direction, lean] = position.split('-')

  useEffect(() => {
    return () => {
      document.removeEventListener('click', documentClickHandler)
    }
  }, [])

  useEffect(() => {
    if (disabled && isOpen) close()
  }, [disabled])

  useEffect(() => {
    if (defaultOpenState) {
      onOpenDropdown()
    }
  }, [])

  useLayoutEffect(() => {
    if (isOpen) {
      if (contentEl?.current) contentEl.current.focus()
      const viewHeight = window.innerHeight
      const rect = contentEl.current!.getBoundingClientRect()
      setOpenAbove(
        (rect.top + rect.height > viewHeight && direction === 'bottom') ||
          direction === 'top',
      )
      setHorizontalAbove(
        rect.top + rect.height > viewHeight &&
          ['right', 'left'].includes(direction),
      )
    }
  }, [isOpen, direction])

  useLayoutEffect(() => {
    if (
      fixedContent &&
      dropdownEl &&
      dropdownEl.current &&
      contentEl &&
      contentEl.current
    ) {
      const dropdownRect = dropdownEl.current.getBoundingClientRect()
      const contentRect = contentEl.current.getBoundingClientRect()
      const viewWidth = window.innerWidth
      const newStyles: Record<string, any> = {}

      if (['right', 'left'].includes(direction)) {
        if (direction === 'left') {
          newStyles.right = cover
            ? viewWidth - dropdownRect.right
            : viewWidth - dropdownRect.left
        } else if (direction === 'right') {
          newStyles.left = cover ? dropdownRect.left : dropdownRect.right
        }
        if (lean === 'top') {
          newStyles.bottom = window.innerHeight - dropdownRect.bottom
        } else newStyles.top = dropdownRect.top
      } else {
        if (direction === 'top' || openAbove) {
          newStyles.top = cover
            ? dropdownRect.top + dropdownRect.height - contentRect.height
            : dropdownRect.top - contentRect.height
        } else {
          newStyles.top = cover
            ? dropdownRect.top
            : dropdownRect.top + dropdownRect.height
        }
        if (lean === 'left') {
          newStyles.right = viewWidth - dropdownRect.right
        } else newStyles.left = dropdownRect.left
      }
      if (contentWidth === 'block') {
        newStyles.width = dropdownRect.width
      }
      if (contentWidth === 'auto') {
        newStyles.width = 'auto'
      }
      if (!isEqual(newStyles, positionStyles)) {
        setPositionStyles(newStyles)
      }
    }
  })

  function doesContain(parent: Node, child: Node | null): boolean {
    if (!child || !(child as Element).classList) return false
    if ((child as Element).classList.contains('dropdown-content')) return true
    if (!child.parentNode && child.nodeName !== '#document') return true
    return doesContain(parent, child.parentNode)
  }

  function documentClickHandler(e: MouseEvent) {
    if (!dropdownEl || !dropdownEl.current || !triggerEl.current) return
    const contains =
      dropdownEl.current.contains(e.target as Node) ||
      doesContain(dropdownEl.current, e.target as Node)
    const isTrigger = triggerEl.current.contains(e.target as Node)
    if (isTrigger) return
    if ((contains && !allowContentClicks) || !contains) {
      onCloseDropdown()
    }
  }

  function toggle(e?: React.MouseEvent) {
    if (isOpen) {
      onCloseDropdown(e)
    } else if (!isOpen) {
      onOpenDropdown(e)
    }
  }

  function onOpenDropdown(e?: React.MouseEvent) {
    if (e) e.stopPropagation()
    if (disabled) return false
    setIsOpen(true)
    document.addEventListener('click', documentClickHandler)
    if (onOpen) onOpen(e)
  }

  function onCloseDropdown(e?: React.MouseEvent) {
    if (e) e.stopPropagation()
    setIsOpen(false)
    setOpenAbove(false)
    setHorizontalAbove(false)
    document.removeEventListener('click', documentClickHandler)
    if (onClose) onClose(e)
  }

  function close(e?: React.MouseEvent) {
    onCloseDropdown(e)
  }

  if (dropdownRef) {
    const current = {
      open: onOpenDropdown,
      close: onCloseDropdown,
      toggle,
      isOpen,
    }
    if (typeof dropdownRef === 'function') {
      dropdownRef(current as any)
    } else {
      ;(dropdownRef as React.MutableRefObject<DropdownRef>).current = current
    }
  }

  const widthStyle = isNumber(contentWidth)
    ? { width: `${contentWidth / 10}rem` }
    : {}

  const widthClass =
    contentWidth === 'md'
      ? 'w-[28rem]'
      : contentWidth === 'lg'
        ? 'w-[40rem]'
        : contentWidth === 'block'
          ? 'w-full'
          : contentWidth === 'auto'
            ? 'w-auto'
            : 'w-72'

  const positionClasses = getContentPositionClasses(
    fixedContent,
    direction,
    lean,
    openAbove,
    horizontalAbove,
    cover,
  )

  const buildContent = () => (
    <div
      ref={contentEl}
      onClick={e => e.stopPropagation()}
      className={`dropdown-content ${fixedContent ? 'fixed z-[10000]' : 'absolute z-[99]'} bg-white rounded shadow-md cursor-default overflow-auto ${widthClass} ${!isOpen ? 'hidden' : ''} ${positionClasses}`}
      style={{
        maxHeight: contentHeight,
        ...widthStyle,
        ...positionStyles,
      }}
    >
      {(renderContent || content)!({ isOpen, close: onCloseDropdown })}
    </div>
  )

  return (
    <div
      className={`relative ${inline ? 'inline-block' : triggerIsBlock ? 'block' : 'inline-flex'} ${className || ''}`}
      ref={dropdownEl}
    >
      <span
        ref={triggerEl}
        className={triggerIsBlock ? 'block' : 'inline-flex'}
      >
        {renderTrigger({
          isOpen,
          toggle,
          open: onOpenDropdown,
          close: onCloseDropdown,
        })}
      </span>
      {fixedContent
        ? createPortal(buildContent(), document.body)
        : buildContent()}
    </div>
  )
}

export const Dropdown = forwardRef(_Dropdown)
